import { triniumApi } from '@api/Trinium/slice';
import { BaseApiRequest } from '@api/Trinium/types';
import {
	Document,
	DocumentType,
	FetchDocumentResponse,
	PostDocumentRequest,
	PutDocumentRequest,
	PutDocumentsRequest,
	transformDocumentsResponse
} from './types';

export const documentsSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchDocumentsApproval: builder.query<Document[], BaseApiRequest>({
				query: ({ terminalNumber }) => `${terminalNumber}/documents`
			}),
			fetchDocuments: builder.query<
				Document[],
				BaseApiRequest & {
					consignmentId: number;
				}
			>({
				query: (params) => `${params.terminalNumber}/consignments/${String(params.consignmentId)}/documents`,
				transformResponse: transformDocumentsResponse
			}),
			fetchDocument: builder.query<
				FetchDocumentResponse,
				BaseApiRequest & {
					id: number;
				}
			>({
				query: (params) => `${params.terminalNumber}/documents/${String(params.id)}`
			}),
			postDocument: builder.mutation<Document[], PostDocumentRequest>({
				query: ({ terminalNumber, fileData, docId, docTypeId, csnId }) => {
					const formData = new FormData();
					if (fileData) {
						formData.append('filedata', fileData);
					}
					if (docId) {
						formData.append('docId', docId.toString());
					}
					formData.append('docTypeId', docTypeId);
					formData.append('csnId', csnId.toString());

					return {
						url: `${terminalNumber}/documents`,
						method: 'POST',
						body: formData
					};
				},
				transformResponse: transformDocumentsResponse,
				async onQueryStarted(params, { dispatch, queryFulfilled }) {
					const result = await queryFulfilled;
					dispatch(
						documentsSlice.util.updateQueryData(
							'fetchDocuments',
							{
								terminalNumber: params.terminalNumber,
								consignmentId: params.csnId
							},
							(draft) => {
								Object.assign(draft, result.data);
							}
						)
					);
				}
			}),
			putDocument: builder.mutation<Document[], PutDocumentRequest>({
				query: ({ actionType, ...params }) => ({
					url: `${params.terminalNumber}/documents/${String(params.id)}`,
					method: 'PUT',
					body: {
						action: actionType.action,
						data: actionType.data
					}
				}),
				transformResponse: transformDocumentsResponse,
				async onQueryStarted(params, { dispatch, queryFulfilled }) {
					const result = await queryFulfilled;

					/** Update the Fetch Document API with updated Document */
					dispatch(
						documentsSlice.util.updateQueryData(
							'fetchDocuments',
							{
								terminalNumber: params.terminalNumber,
								consignmentId: params.consignmentId
							},
							() => result.data
						)
					);

					// update any rows for Document Control
					result.data.forEach((document) => {
						dispatch(
							documentsSlice.util.updateQueryData(
								'fetchDocumentsApproval',
								{
									terminalNumber: params.terminalNumber
								},
								(draft) => {
									const d = draft.find((d) => d.id === document.id);
									if (d) {
										Object.assign(d, document);
									}
								}
							)
						);
					});
				}
			}),
			putDocuments: builder.mutation<Document[], PutDocumentsRequest>({
				query: ({ actionType, ...params }) => ({
					url: `${params.terminalNumber}/documents`,
					method: 'PUT',
					body: {
						action: actionType.action,
						data: actionType.data
					}
				}),
				transformResponse: transformDocumentsResponse,
				async onQueryStarted(params, { dispatch, queryFulfilled }) {
					const result = await queryFulfilled;
					if (params.actionType.action === 'unscan' && params.actionType.data.csnId) {
						/** Update the Fetch Document API with each updated Document */
						dispatch(
							documentsSlice.util.updateQueryData(
								'fetchDocuments',
								{
									terminalNumber: params.terminalNumber,
									consignmentId: params.actionType.data.csnId
								},
								(draft) => {
									Object.assign(draft, result.data);
								}
							)
						);
					} else if (params.actionType.action === 'deleteDocuments' && params.consignmentId) {
						// delete document placeholder action
						const idsToRemove = params.actionType.data.docIds;
						dispatch(
							documentsSlice.util.updateQueryData(
								'fetchDocuments',
								{
									terminalNumber: params.terminalNumber,
									consignmentId: params.consignmentId
								},
								(draft) => {
									return draft.filter((row) => !idsToRemove.includes(row.id));
								}
							)
						);
					} else if (['approveDocuments', 'deleteDocuments'].includes(params.actionType.action)) {
						// approve or delete document action in Document Control
						dispatch(
							documentsSlice.util.updateQueryData(
								'fetchDocumentsApproval',
								{
									terminalNumber: params.terminalNumber
								},
								() => result.data
							)
						);
					}
				}
			}),
			fetchDocumentTypes: builder.query<DocumentType[], BaseApiRequest>({
				query: ({ terminalNumber }) => ({
					url: `${terminalNumber}/documents`,
					method: 'PUT',
					body: {
						action: 'getDocumentTypes'
					}
				})
			})
		};
	}
});

export const {
	useFetchDocumentsApprovalQuery,
	useFetchDocumentsQuery,
	useLazyFetchDocumentsQuery,
	useFetchDocumentTypesQuery,
	usePostDocumentMutation,
	usePutDocumentMutation,
	usePutDocumentsMutation
} = documentsSlice;
